import type { ChainFamily } from 'src/blockchain';
import type { ApiAvailableChain } from 'src/types/chain';

import { ENABLED_FAMILIES } from 'src/blockchain';

/**
 * Whether a chain from `GET /buyer/chains` takes new money: a new Buy Now, or a new escrow deal in
 * either vertical. Both flags read "absent" as unknown, never as closed — an api older than a flag
 * answers nothing for it, and the server has the last word on every payment anyway.
 */
export const takesNewMoney = (chain: ApiAvailableChain): boolean =>
  chain.purchases !== false ||
  chain.escrowVerticals === undefined ||
  chain.escrowVerticals.length > 0;

interface LinkableFamiliesParams {
  /**
   * `GET /buyer/chains`, or `[]` while there is no answer — still on its way, or failed. Either
   * way only the wallets the account holds are shown: a slot that appears and vanishes a moment
   * later is noise, and on a failed request the build's families would put TRON back in front of
   * a new account. An api older than the flags still answers a list; `takesNewMoney` reads the
   * missing flags as unknown.
   */
  buyerChains: ApiAvailableChain[];
  /** Families the account already holds a wallet in. */
  linkedFamilies?: ChainFamily[];
  /** Families whose wallet stack this build carries; a parameter so it can be tested without a rebuild. */
  buildFamilies?: ChainFamily[];
}

/**
 * The wallet families an account is offered a slot for — after registration and in Profile.
 *
 * A wallet is linked to pay with it. Since api#401 TRON takes no new Buy Now, and its escrow is
 * paused, yet it stays in the build the storefront ships until staging is switched to Base alone
 * (one-rail spec §3, M4b). A new account offered a TRON slot could link a wallet that pays for
 * nothing — and the first wallet an account links becomes its primary one for good. So a slot is
 * offered for a family the api takes new money in, and kept for a family the account already
 * holds: its wallet stays in sight, next to the deals it paid for.
 *
 * The region gate is the api's too: a chain `GET /buyer/chains` leaves out for this caller offers
 * no slot either.
 */
export function linkableFamilies(params: LinkableFamiliesParams): ChainFamily[] {
  const { buyerChains, linkedFamilies = [], buildFamilies = ENABLED_FAMILIES } = params;
  const open = new Set(buyerChains.filter(takesNewMoney).map((chain) => chain.family));

  return buildFamilies.filter((family) => open.has(family) || linkedFamilies.includes(family));
}
